import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Archive, Check, Download, FileJson, List, ScrollText, Square, X } from "lucide-react";
import Panel from "@ui/panel/Panel";
import { GD } from "src/GD";
import { BATCH_DEFAULT_MAX_TRANSACTIONS, BATCH_EXPORT_USER_IDS, BATCH_STAGES, collectTransactionBatch, type BatchProgress, type BatchResult, type BatchStage } from "src/services/transactionBatchExport";
import { createTransactionBatchZip } from "src/services/transactionBatchZip";

export interface TransactionBatchSnapshot {
	projectId: number;
	userId: number;
	filters: Record<string, unknown>;
	expectedCount?: number;
}
const stageInfo: Record<BatchStage, { title: string; description: string; icon: typeof List }> = {
	list: { get title() { return msg("transactionList"); }, get description() { return msg("matchingTransactionsUpToTheSelectedLimit"); }, icon: List },
	transactions: { get title() { return msg("transactionDetails"); }, get description() { return msg("fullJsonForEachPayment"); }, icon: FileJson },
	processing: { get title() { return msg("dectaDetails"); }, get description() { return msg("processingSessionsAndPayloadsWhereAvailable"); }, icon: FileJson },
	logs: { get title() { return msg("logs"); }, get description() { return msg("transactionLogsAndEveryPageOfDectaLogs"); }, icon: ScrollText },
	archive: { get title() { return msg("zipArchive"); }, get description() { return msg("csvJsonFilesAndACompletenessManifest"); }, icon: Archive },
};
function remainingTime(ms: number | null | undefined) {
	if (ms == null || !Number.isFinite(ms)) return msg("estimatingRemainingTime");
	const minutes = Math.ceil(ms / 60000);
	if (minutes <= 1) return msg("aboutAMinuteRemaining");
	if (minutes < 60) return msg("templateAboutMinRemaining_67e6427a", { v0: minutes });
	return msg("templateAboutHRemaining_fbe77651", { v0: Math.floor(minutes / 60), v1: minutes % 60 ? msg("templateMin_99e59f9f", { v0: minutes % 60 }) : "" });
}
const Header = styled.header`
	display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-shrink: 0;
	padding: 20px 24px 16px; border-bottom: 1px solid #edf0f5; background: #fff;
	font: 13px/1.5 "Euclid Circular B", sans-serif;
	.context { font-size: 11px; color: #667085; margin-bottom: 5px; }
	h2 { margin: 0; font-size: 21px; font-weight: 600; color: #182230; }
	button { display: grid; place-items: center; width: 32px; height: 32px; border: 0; border-radius: 7px; color: #667085; background: transparent; cursor: pointer; }
	button:hover { background: #f5f7fa; }
`;
const Body = styled.div`
	min-height: 0; overflow: auto; padding: 20px 24px; background: #f5f7fa;
	display: flex; flex-direction: column; gap: 18px; font: 13px/1.5 "Euclid Circular B", sans-serif; color: #344054;
	.card { background: #fff; padding: 18px 20px; border: 1px solid #d0d5dd; border-radius: 12px; box-shadow: 0 1px 2px rgba(16,24,40,.05); }
	h3 { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #667085; margin: 0 0 10px; }
	p { margin: 0; } p + p { margin-top: 8px; }
	.filters { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
	.filter { padding: 3px 8px; border: 1px solid #e4e7ec; border-radius: 6px; font-size: 12px; overflow-wrap: anywhere; }
	.part { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 0; border-bottom: 1px solid #edf0f5; }
	.part:last-child { border-bottom: 0; }
	.part button { display: inline-flex; align-items: center; gap: 6px; padding: 7px 10px; border: 1px solid #d0d5dd; border-radius: 8px; background: white; color: #344054; font: inherit; cursor: pointer; }
	.limit-field { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; margin: 14px 0 8px; }
	.limit-field input { box-sizing: border-box; width: 180px; max-width: 100%; padding: 8px 10px; border: 1px solid #d0d5dd; border-radius: 8px; font: inherit; color: #344054; background: #fff; }
	.limit-field input:focus { outline: 2px solid #84adff; outline-offset: 2px; }
	.step { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid #edf0f5; }
	.step:last-child { border-bottom: 0; }
	.step-icon { display: grid; place-items: center; flex: 0 0 32px; height: 32px; background: #f2f4f7; border-radius: 8px; color: #667085; }
	.step[data-state="active"] .step-icon { color: #175cd3; background: #eff4ff; }
	.step[data-state="done"] .step-icon { color: #067647; background: #ecfdf3; }
	.step strong { display: block; font-weight: 600; }
	.muted { color: #667085; font-size: 12px; }
	.step-state { margin-left: auto; color: #667085; font-size: 12px; white-space: nowrap; }
	progress { display: block; width: 100%; height: 8px; margin: 12px 0; accent-color: #175cd3; }
	.metrics { display: flex; justify-content: space-between; gap: 10px; flex-wrap: wrap; color: #667085; font-size: 12px; font-variant-numeric: tabular-nums; }
	.error { border-color: #fecdca; color: #b42318; background: #fff6f5; }
	.success { border-color: #abefc6; background: #f6fef9; }
	@media(max-width: 600px) { padding: 16px; .card { padding: 16px; } }
`;
const Footer = styled.footer`
	display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; padding: 16px 24px; flex-shrink: 0; background: #fff; border-top: 1px solid #edf0f5;
	button { display: inline-flex; align-items: center; justify-content: center; gap: 7px; padding: 9px 14px; border: 1px solid #d0d5dd; border-radius: 8px; background: #fff; color: #344054; font: 500 13px/1.5 "Euclid Circular B", sans-serif; cursor: pointer; }
	button.primary { background: #182230; border-color: #182230; color: #fff; }
	button:disabled { opacity: .5; cursor: default; }
`;

export function TransactionBatchExportPopup({ snapshot, onClose }: { snapshot: TransactionBatchSnapshot; onClose: () => void }) {
	useLocale();
	const [phase, setPhase] = useState<"idle" | "collecting" | "packing" | "ready">("idle");
	const [progress, setProgress] = useState<BatchProgress | null>(null);
	const [result, setResult] = useState<BatchResult["manifest"] | null>(null);
	const [error, setError] = useState("");
	const [blob, setBlob] = useState<Blob | null>(null);
	const [parts, setParts] = useState<{ number: number; name: string; blob: Blob; final: boolean; downloaded: boolean }[]>([]);
	const [elapsed, setElapsed] = useState(0);
	const [excludeInsufficientFunds, setExcludeInsufficientFunds] = useState(true);
	const [maxTransactionsInput, setMaxTransactionsInput] = useState(String(BATCH_DEFAULT_MAX_TRANSACTIONS));
	const maxTransactions = Number(maxTransactionsInput);
	const validLimit = Number.isSafeInteger(maxTransactions) && maxTransactions > 0;
	const controller = useRef<AbortController | null>(null);
	const lifetime = useRef<AbortController | null>(null);
	const popup = useRef<HTMLDivElement>(null);
	const started = useRef(false);
	const startedAt = useRef(0);
	const [allowed, setAllowed] = useState(false);
	const closeRef = useRef(onClose); closeRef.current = onClose;
	useEffect(() => {
		const alive = new AbortController(); lifetime.current = alive;
		const previous = document.activeElement;
		popup.current?.focus();
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => {
			alive.abort(); controller.current?.abort(); closeRef.current();
		});
		const projectChanged = GD.S_PROJECT_SELECTED.subscribe(project => {
			if (project?.id !== snapshot.projectId) {
				alive.abort(); controller.current?.abort(); closeRef.current();
			}
		});
		// Profile/project requests resolve asynchronously. An unresolved context
		// must not be treated as a denied context on the initial render.
		void checkContext().then(() => {
			if (!alive.signal.aborted) setAllowed(true);
		}).catch(() => {
			if (!alive.signal.aborted) { alive.abort(); closeRef.current(); }
		});
		return () => {
			alive.abort(); controller.current?.abort();
			GD.S_AUTH_LOG_OUT.unsubscribe(logout);
			GD.S_PROJECT_SELECTED.unsubscribe(projectChanged);
			if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
		};
	}, []);
	useEffect(() => { if (allowed) popup.current?.focus(); }, [allowed]);
	const busy = phase === "collecting" || phase === "packing";
	useEffect(() => {
		if (!busy) return;
		const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)), 1000);
		return () => clearInterval(timer);
	}, [busy]);
	const close = () => { lifetime.current?.abort(); controller.current?.abort(); onClose(); };
	const checkContext = async () => {
		const profile = await GD.REQ_PROFILE.request();
		const selectedProject = await GD.REQ_CURRENT_PROJECT.request();
		if (profile?.id !== snapshot.userId || selectedProject?.id !== snapshot.projectId
			|| !(BATCH_EXPORT_USER_IDS as readonly number[]).includes(profile.id)) {
			throw new Error(msg("theUserOrProjectChangedCollectionStopped"));
		}
	};
	const start = async () => {
		if (!allowed || !validLimit || started.current || !lifetime.current) return;
		started.current = true;
		const alive = lifetime.current;
		const active = new AbortController(); controller.current = active;
		startedAt.current = Date.now();
		setPhase("collecting"); setError(""); setElapsed(0);
		try {
			await checkContext();
			const collected = await collectTransactionBatch({
				...snapshot, excludeInsufficientFunds, maxTransactions, signal: active.signal, checkContext,
				request: request => GD.REQ_API.request(request), onProgress: value => { if (!alive.signal.aborted) setProgress(value); },
				onPart: async (files, exportId, number) => {
					const packed = await createTransactionBatchZip(files, alive.signal, () => {});
					alive.signal.throwIfAborted();
					await checkContext();
					setParts(previous => [...previous, { number, name: `${exportId}-part-${String(number).padStart(3, "0")}.zip`, blob: packed, final: false, downloaded: false }]);
				},
			});
			alive.signal.throwIfAborted();
			await checkContext();
			setResult(collected.manifest);
			setPhase("packing");
			setProgress({ stage: "archive", completed: 0, total: Object.keys(collected.files).length, requests: collected.manifest.requests, message: msg("preparingZipArchive") });
			const packingStartedAt = Date.now();
			// Capture sizes before compression transfers the buffers to its worker.
			const sizes = Object.values(collected.files).map(file => file.byteLength);
			const totalBytes = sizes.reduce((sum, size) => sum + size, 0);
			let packedBytes = 0, packedFiles = 0;
			const archive = await createTransactionBatchZip(collected.files, alive.signal, (done, total) => {
				while (packedFiles < done) packedBytes += sizes[packedFiles++];
				setProgress({
					stage: "archive", completed: done, total, requests: collected.manifest.requests, message: msg("templatePackingFilesOf_89b70bf9", { v0: done, v1: total }),
					estimatedRemainingMs: packedBytes && Date.now() - packingStartedAt >= 1000
						? (Date.now() - packingStartedAt) * (totalBytes - packedBytes) / packedBytes : null,
				});
			});
			alive.signal.throwIfAborted();
			const number = collected.manifest.parts.length;
			setParts(previous => [...previous, { number, name: `${collected.manifest.exportId}-part-${String(number).padStart(3, "0")}-FINAL${collected.manifest.status === "complete" ? "" : "-PARTIAL"}.zip`, blob: archive, final: true, downloaded: false }]);
			setBlob(archive); setPhase("ready");
		} catch (reason) {
			if (!alive.signal.aborted) { setError(reason instanceof Error ? reason.message : msg("couldNotPrepareTheArchive")); setPhase("ready"); }
		} finally { controller.current = null; }
	};
	const download = (part = parts[parts.length - 1]) => {
		if (!part || !allowed) return;
		const url = URL.createObjectURL(part.blob);
		const anchor = document.createElement("a");
		anchor.href = url;
		anchor.download = part.name;
		anchor.click();
		setParts(previous => previous.map(item => item.number === part.number ? { ...item, downloaded: true } : item));
		setTimeout(() => URL.revokeObjectURL(url), 30000);
	};
	if (!allowed) return null;
	const stageIndex = progress ? BATCH_STAGES.indexOf(progress.stage) : -1;
	const stoppedIndex = result?.stoppedAt ? BATCH_STAGES.indexOf(result.stoppedAt.stage) : -1;
	const percent = progress?.total ? Math.min(100, Math.round(progress.completed / progress.total * 100)) : null;
	return createPortal(<div ref={popup} role="dialog" aria-modal="true" aria-label={msg("batchAnalysis")} tabIndex={-1} onKeyDown={event => {
		if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(); }
		if (event.key !== "Tab") return;
		const controls = Array.from(popup.current?.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled)") ?? []);
		if (!controls.length) return;
		const first = controls[0], last = controls[controls.length - 1];
		if (event.shiftKey && (document.activeElement === first || document.activeElement === popup.current)) { event.preventDefault(); last.focus(); }
		else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
	}}>
		<Panel growContent disableScroll decorator="absolute" onClose={close} sx={{ padding: 0, width: "760px", maxWidth: "94vw", maxHeight: "90dvh", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
			<Header><div><div className="context">{msg("transactionsDataExport")}</div><h2>{msg("batchAnalysis")}</h2></div>
				<button type="button" aria-label={busy ? msg("cancelAndClose") : msg("close")} title={busy ? msg("closeAndDiscardCollectedData") : msg("close")} onClick={close}><X size={18} /></button>
			</Header>
			<Body>
				<section><h3>{msg("selection")}</h3><div className="card">
					<p><strong>{snapshot.expectedCount != null ? msg("templateTransactionsInTheCurrentList_cb5f7ca0", { v0: snapshot.expectedCount.toLocaleString() }) : msg("allTransactionsMatchingTheCurrentFilters")}</strong></p>
					<p className="muted">{msg("filtersAreCapturedWhenThisWindowOpensCollectionStartsFromTheFirstPage")}</p>
					<label className="limit-field">{msg("maximumTransactions")}<input type="number" min={1} step={1} value={maxTransactionsInput} disabled={phase !== "idle"}
							aria-invalid={!validLimit} aria-describedby="batch-limit-description"
							onChange={event => setMaxTransactionsInput(event.target.value)} />
					</label>
					<p className="muted" id="batch-limit-description">{validLimit
						? msg("templateCollectUpToTransactionsInListOrderThenLoadTheirDetailsAn_055b3644", { v0: maxTransactions.toLocaleString() })
						: msg("enterAPositiveWholeNumber")}</p>
					<label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
						<input type="checkbox" checked={excludeInsufficientFunds} disabled={phase !== "idle"} onChange={event => setExcludeInsufficientFunds(event.target.checked)} />{msg("excludeInsufficientFundsOverCreditLimit")}</label>
					<p className="muted">{msg("skipsFailedPaymentsWithThisExplicitReasonCheckingTheListFirs_bc666cca")}</p>
					<div className="filters">{Object.entries(snapshot.filters).filter(([key, value]) => key !== "pagination" && value != null && value !== "").map(([key, value]) =>
						<span className="filter" key={key}>{key.replace(/_/g, " ")}: {Array.isArray(value) ? value.join(", ") : String(value)}</span>)}</div>
				</div></section>
				<section><h3>{msg("collectionStages")}</h3><div className="card">
					{BATCH_STAGES.map((stage, index) => {
						const state = stoppedIndex >= 0 && index >= stoppedIndex && stage !== "archive" ? "stopped"
							: (phase === "ready" && blob) || index < stageIndex ? "done" : index === stageIndex ? "active" : "waiting";
						const Icon = state === "done" ? Check : stageInfo[stage].icon;
						return <div className="step" data-state={state} key={stage}><span className="step-icon"><Icon size={17} /></span>
							<div><strong>{stageInfo[stage].title}</strong><span className="muted">{stageInfo[stage].description}</span></div>
							<span className="step-state">{state === "done" ? msg("done") : state === "stopped" ? msg("incomplete") : state === "active" ? percent == null ? msg("loading_dc380888") : `${percent}%` : msg("waiting")}</span>
						</div>;
					})}
				</div></section>
				{progress && busy && <div className="card"><p role="status">{progress.message}</p><progress aria-label={stageInfo[progress.stage].title} max={100} value={percent ?? undefined} />
					<div className="metrics"><span>{progress.requests}{msg("requests")}</span><span>{Math.floor(elapsed / 60)}{msg("m")}{elapsed % 60}{msg("sElapsed")}</span></div>
					<p style={{ marginTop: 10 }}><strong>{progress.retryAt
						? msg("templateWaitingForConnectionRetryInS_2295aac4", { v0: progress.retryAttempt, v1: Math.max(0, Math.ceil((progress.retryAt - Date.now()) / 1000)) })
						: remainingTime(progress.estimatedRemainingMs)}</strong></p>
					<p className="muted">{phase === "packing"
						? msg("estimatedFromArchivePackingSpeed")
						: msg("estimatedForAllRemainingCollectionStagesIncludingRequestPaus_39412d9b")}</p>
				</div>}
				{result && <div className={`card ${result.status === "complete" ? "success" : "error"}`} role="status">
					<strong>{result.status === "complete" ? msg("dataCollectionComplete") : msg("partialCollection")}</strong>
					<p>{result.listed}{msg("transactionsListed")}{result.excluded}{msg("excluded")}{result.items.filter(item => item.transaction === "complete").length}{msg("details_f2ab1605")}{result.requests}{msg("requests")}</p>
					{result.limitReached && <p>{msg("selectionLimitedToTheFirst")}{result.maxTransactions.toLocaleString()}{msg("transactions_b67b5813")}{result.expectedTotal !== null ? msg("templateOf_e2b323d6", { v0: result.expectedTotal.toLocaleString() }) : ""}.</p>}
					{result.error && <p>{result.error}</p>}
					{blob && <p>{parts.length} ZIP {parts.length === 1 ? msg("part_37a68013") : msg("parts")} · {(parts.reduce((sum, part) => sum + part.blob.size, 0) / 1024 / 1024).toFixed(1)}{msg("mbTotal")}{result.status === "complete" ? msg("downloadAllPartsBelow") : msg("theFinalManifestIdentifiesMissingFilesAndTheStoppingPoint")}</p>}
				</div>}
				{parts.length > 0 && <section><h3>{msg("archiveParts")}</h3><div className="card">
					<p>{msg("downloadEveryPartBeforeClosingThisWindowAllFilesShareOneExpo_6266c3dd")}</p>
					{parts.map(part => <div className="part" key={part.number}>
						<div><strong>{msg("part")}{String(part.number).padStart(3, "0")}{part.final ? msg("final") : ""}</strong><div className="muted">{(part.blob.size / 1024 / 1024).toFixed(1)}{msg("mb")}{part.downloaded ? msg("downloadRequested") : msg("ready")}</div></div>
						<button type="button" onClick={() => download(part)}><Download size={14} />{part.downloaded ? msg("downloadAgain") : msg("download")}</button>
					</div>)}
				</div></section>}
				{error && <div className="card error" role="alert">{error}</div>}
				<p className="muted">{msg("oneRequestAtATimeWithA100MsPauseAfterEachResponseConnectionF_83c3a81c")}</p>
			</Body>
			<Footer>
				<button type="button" onClick={close}>{busy ? msg("closeDiscard") : msg("close")}</button>
				{phase === "idle" && <button type="button" className="primary" disabled={!validLimit} onClick={() => void start()}><Download size={16} />{msg("startCollection")}</button>}
				{phase === "collecting" && <button type="button" onClick={() => controller.current?.abort()}><Square size={14} />{msg("stopKeepCollected")}</button>}
				{blob && <button type="button" className="primary" onClick={() => download()}><Download size={16} />{parts.length > 1 ? msg("downloadFinalPart") : result?.status === "complete" ? msg("downloadZip") : msg("downloadPartialZip")}</button>}
			</Footer>
		</Panel>
	</div>, document.body);
}
